import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import FormFillFields, { FormLocaleSwitch } from './FormFillFields'
import FormBrandingHeader, { FormTenantLogo, formSubmitButtonStyle } from './FormBrandingHeader'
import {
  DEFAULT_LOCALE,
  afterSubmitRedirect,
  formCardMaxWidth,
  formPageBackgroundIsDark,
  formPageStyle,
  normalizeLocales,
  resolveConfirmMessage,
  resolveFormDisplayTitle,
  resolveFormSubmitLabel,
  shouldShowTenantLogo,
  shouldUseClubColors,
} from './formBuilderModel'

function parseAnswers(raw) {
  if (!raw || typeof raw !== 'object') return {}
  const { schemaVersion, ...rest } = raw
  return rest
}

export default function FormAssignmentDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [bundle, setBundle] = useState(null)
  const [answers, setAnswers] = useState({})
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [busy, setBusy] = useState(false)
  const [locale, setLocale] = useState(DEFAULT_LOCALE)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const load = useCallback(async () => {
    setError('')
    const res = await authFetch(getLocalApiUrl(`/api/forms/assignments/${id}`))
    const j = await res.json()
    if (!res.ok || !j.success) {
      setError(j.error || 'Failed to load assignment')
      return
    }
    setBundle(j.data)
    const locales = normalizeLocales(j.data.version?.settings)
    setLocale(locales[0] || DEFAULT_LOCALE)
    const submissionAnswers = parseAnswers(j.data.submission?.answers)
    const draftAnswers = parseAnswers(j.data.draft?.answers)
    setAnswers({ ...submissionAnswers, ...draftAnswers })
  }, [id])

  useEffect(() => { load() }, [load])

  const settings = bundle?.version?.settings
  const a = bundle?.assignment
  const workflow = bundle?.workflow
  const currentStep = workflow?.currentStep
  const closed = ['accepted', 'cancelled', 'rejected'].includes(a?.status)
  const isReviewPhase = !!bundle?.submission && a?.status === 'in_review' && (!currentStep || currentStep.type === 'approve')
  const isWorkflowFillPhase = !!bundle?.submission
    && currentStep?.type === 'fill'
    && ['assigned', 'sent', 'opened', 'in_progress', 'overdue'].includes(a?.status)
  const fillRole = isReviewPhase ? 'review' : 'submitter'
  const visibleSectionIds = useMemo(() => {
    if (!isWorkflowFillPhase || !currentStep?.sections?.length) return null
    return currentStep.sections
  }, [isWorkflowFillPhase, currentStep])
  const canSubmit = !closed && !bundle?.submission
  const canWorkflowFillSubmit = isWorkflowFillPhase && !closed
  const canReview = isReviewPhase && !closed

  const onChange = (fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }))

  const saveDraft = async () => {
    setBusy(true)
    setError('')
    setErrors({})
    try {
      const res = await authFetch(getLocalApiUrl(`/api/forms/assignments/${id}/draft`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Draft failed')
      setInfo('Draft saved')
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const submit = async () => {
    setBusy(true)
    setError('')
    setErrors({})
    try {
      const res = await authFetch(getLocalApiUrl(`/api/forms/assignments/${id}/submit`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
      })
      const j = await res.json()
      if (res.status === 422 && j.errors) {
        setErrors(Object.fromEntries(j.errors.map((e) => [e.fieldId, e.message])))
        throw new Error(j.error || 'Please fix the highlighted fields')
      }
      if (!res.ok || !j.success) throw new Error(j.error || 'Submit failed')
      const nextRef = j.data.submission?.ref || ''
      const dest = afterSubmitRedirect(settings, nextRef)
      setInfo(resolveConfirmMessage(settings, {
        locale,
        answers,
        definition: bundle?.version?.definition,
        formName: a?.formName,
        ref: nextRef,
      }) + (nextRef ? ` — ${nextRef}` : ''))
      if (dest) window.setTimeout(() => { window.location.assign(dest) }, 1600)
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const act = async (action, body = {}) => {
    setBusy(true)
    setError('')
    setErrors({})
    try {
      const res = await authFetch(getLocalApiUrl(`/api/forms/assignments/${id}/${action}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await res.json()
      if (res.status === 422 && j.errors) {
        setErrors(Object.fromEntries(j.errors.map((e) => [e.fieldId, e.message])))
        throw new Error(j.error || 'Please complete the required fields')
      }
      if (!res.ok || !j.success) throw new Error(j.error || `${action} failed`)
      if (action === 'approve') {
        const next = j.data?.status
        setInfo(next === 'accepted' ? 'Approved — workflow complete' : 'Approved — moved to next step')
        setRejectOpen(false)
        setRejectReason('')
      }
      if (action === 'reject') {
        setInfo('Submission rejected')
        setRejectOpen(false)
        setRejectReason('')
      }
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const confirmReject = () => {
    const reason = rejectReason.trim()
    if (!reason) {
      setError('Enter a rejection reason')
      return
    }
    act('reject', { reason })
  }

  if (!bundle && !error) return <div className="p-6 text-sm text-slate-500">Loading…</div>
  const branding = bundle?.branding
  const showBranding = shouldShowTenantLogo(settings) || shouldUseClubColors(settings)
  const darkPage = !!(bundle?.version && formPageBackgroundIsDark(settings, branding))
  const cardMax = formCardMaxWidth(settings)
  const navLinkCls = darkPage
    ? 'text-sm text-teal-200 hover:text-white'
    : 'text-sm text-teal-700 hover:text-teal-900'
  const metaCls = darkPage ? 'text-sm text-slate-200 mt-1' : 'text-sm text-slate-500 mt-1'
  const testBadge = a?.isTest ? (
    <span className="ml-2 align-middle rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
      Test
    </span>
  ) : null
  const hasNextStep = workflow?.enabled && workflow?.steps?.length > 0 && currentStep
    ? workflow.steps.findIndex((s) => s.id === currentStep.id) < workflow.steps.length - 1
    : false

  return (
    <div className="p-6 min-h-full" style={bundle?.version ? formPageStyle(settings, branding) : undefined}>
      <div className="mx-auto" style={{ maxWidth: cardMax, width: '100%' }}>
      <div className="flex flex-wrap gap-3 mb-3">
        <button type="button" className={navLinkCls} onClick={() => navigate('/compliance-safe/forms/pipeline')}>← Pipeline</button>
        {a?.formId ? (
          <button type="button" className={navLinkCls} onClick={() => navigate(`/compliance-safe/forms/${a.formId}/records`)}>
            All records for this form
          </button>
        ) : null}
      </div>
      {a ? (
        <div className="mb-3">
          {shouldShowTenantLogo(settings) ? (
            <FormTenantLogo settings={settings} branding={branding} darkPage={darkPage} />
          ) : null}
          {!a.isTest ? (
            <p className={metaCls}>
              {a.status} · {a.facing} · {a.subjectLabel || a.subjectType}
              {a.submissionRef ? ` · ${a.submissionRef}` : ''}
              {currentStep?.name ? ` · Step: ${currentStep.name}` : ''}
            </p>
          ) : null}
          {a.isTest ? (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Test run — fill and submit as you would live. The record appears on Form records and the pipeline with a Test badge.
            </p>
          ) : null}
          {isReviewPhase ? (
            <p className="mt-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900">
              Approval step — submitted answers are read-only. Complete reviewer fields if any, then approve or reject.
              {hasNextStep ? ' Approve moves this to the next workflow step.' : ' Approve completes the workflow.'}
            </p>
          ) : null}
          {isWorkflowFillPhase ? (
            <p className="mt-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900">
              Workflow fill step — complete your assigned sections, then submit to continue.
            </p>
          ) : null}
          {a.status === 'rejected' && a.cancelReason ? (
            <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
              Rejected: {a.cancelReason}
            </p>
          ) : null}
        </div>
      ) : null}
      {error ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {info ? <div className="mb-3 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-800 break-all">{info}</div> : null}
      {bundle?.version ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          {a?.facing === 'patron' ? (
            <FormLocaleSwitch settings={bundle.version.settings} locale={locale} onChange={setLocale} className="mb-4" />
          ) : null}
          <FormBrandingHeader
            formName={resolveFormDisplayTitle(settings, a?.formName, a?.facing === 'patron' ? locale : DEFAULT_LOCALE)}
            titleExtra={testBadge}
          />
          <FormFillFields
            definition={bundle.version.definition}
            settings={bundle.version.settings}
            locale={a?.facing === 'patron' ? locale : DEFAULT_LOCALE}
            fillRole={fillRole}
            visibleSectionIds={visibleSectionIds}
            closed={closed}
            answers={answers}
            onChange={onChange}
            errors={errors}
          />
          {rejectOpen ? (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50/60 p-3">
              <label className="block text-xs font-semibold text-red-800 mb-1">Rejection reason</label>
              <textarea
                className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-slate-900"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this submission is rejected…"
              />
              <div className="flex flex-wrap gap-2 mt-2">
                <Button variant="destructive" size="sm" onClick={confirmReject} disabled={busy}>Confirm reject</Button>
                <Button variant="outline" size="sm" onClick={() => { setRejectOpen(false); setRejectReason(''); setError('') }} disabled={busy}>Cancel</Button>
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2 mt-6">
            {canSubmit ? (
              <>
                <Button variant="outline" onClick={saveDraft} disabled={busy}>Save draft</Button>
                <Button onClick={submit} disabled={busy} style={showBranding ? formSubmitButtonStyle(settings, branding) : undefined}>
                  {resolveFormSubmitLabel(settings, a?.facing === 'patron' ? locale : DEFAULT_LOCALE)}
                </Button>
              </>
            ) : null}
            {canWorkflowFillSubmit ? (
              <>
                <Button variant="outline" onClick={saveDraft} disabled={busy}>Save draft</Button>
                <Button onClick={submit} disabled={busy}>Submit step</Button>
              </>
            ) : null}
            {canReview ? (
              <>
                <Button variant="outline" onClick={saveDraft} disabled={busy}>Save review draft</Button>
                <Button onClick={() => act('approve', { answers })} disabled={busy}>
                  {hasNextStep ? 'Approve & next step' : 'Approve'}
                </Button>
                {!rejectOpen ? (
                  <Button variant="destructive" onClick={() => { setRejectOpen(true); setError('') }} disabled={busy}>Reject</Button>
                ) : null}
              </>
            ) : null}
            {a?.assigneeRoleId && !a?.claimedBy && !closed ? (
              <Button
                variant="outline"
                onClick={() => act('claim', { answers })}
                disabled={busy}
              >
                Claim
              </Button>
            ) : null}
            {a?.facing === 'patron' && ['sent', 'opened', 'in_progress', 'overdue'].includes(a.status) ? (
              <Button variant="outline" onClick={() => act('remind')} disabled={busy}>Remind</Button>
            ) : null}
            {!closed ? (
              <Button variant="outline" onClick={() => act('cancel')} disabled={busy}>Cancel assignment</Button>
            ) : null}
          </div>
        </div>
      ) : null}
      </div>
    </div>
  )
}
